import { useEffect, useState } from "react";
import { eventHasPassed } from "@libs/platform-wire";
import type { Purchase } from "@libs/platform-wire/purchase";
import { useRuntime } from "../../../runtime/provider";

/** Why a 1-tap event sells nothing now, as Link in Bio says it
 *  (`use-buy-on-behalf-flow.tsx`): it has passed, or its purchase has no
 *  item left on sale. */
type VendorEventEnd = "passed" | "unavailable";

/** The event's own times, as the access carries them. */
export type VendorEventTimes = {
  startDateTime: string;
  endDateTime: string | null;
  /** `time: null` when only the date was published. */
  startsAtLocal: { time: string | null };
};

export const VENDOR_EVENT_END_WORDS: Record<VendorEventEnd, string> = {
  passed: "This event has passed.",
  unavailable: "Tickets are no longer available.",
};

/** Why a 1-tap event sells nothing at `now`, or null while it can be
 *  bought for (and for an event still to come with no purchase). A passed
 *  event has passed by its own date, whatever its purchase: the access read
 *  offers none for it (a passed event's listing is no checkout's), and its
 *  seller's link sells nothing either. */
function vendorEventEnd(
  purchase: Pick<Purchase, "picker"> | null | undefined,
  event: VendorEventTimes,
  now: number,
): VendorEventEnd | null {
  if (
    eventHasPassed(
      {
        startsAt: event.startDateTime,
        endsAt: event.endDateTime,
        startTimeKnown: event.startsAtLocal.time !== null,
      },
      now,
    )
  )
    return "passed";
  if (!purchase) return null;
  return purchase.picker.items.every((item) => !item.available) ? "unavailable" : null;
}

/** `vendorEventEnd` on the runtime's clock, read again every minute: an
 *  event left open passes while it is shown. */
export function useVendorEventEnd(
  purchase: Pick<Purchase, "picker"> | null | undefined,
  event: VendorEventTimes | null,
): VendorEventEnd | null {
  const { clock } = useRuntime().ports;
  const [now, setNow] = useState(() => clock.now());
  useEffect(() => {
    let handle = clock.setTimeout(function tick() {
      setNow(clock.now());
      handle = clock.setTimeout(tick, 60_000);
    }, 60_000);
    return () => clock.clearTimeout(handle);
  }, [clock]);
  return event ? vendorEventEnd(purchase, event, now) : null;
}
